import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { ApiError } from '../../../services/api';
import { getBillingSettings, saveBillingSettings } from '../../../services/admin-billing';
import type { BillingSettings } from '../../../types/adminBilling';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import './Billing.css';

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/**
 * The Stripe switch, for the whole platform.
 *
 * **Only Stripe.** This screen used to carry the reminder schedule too - whether to email, how many
 * days before due, how often once overdue. Every organization is billed through Stripe now, and
 * Stripe does its own chasing, so that schedule governed nothing anybody could see. The stored
 * values are still sent back unchanged on save, because the endpoint is a PUT.
 *
 * **The Stripe switch is editable, and this is the only place it can be set.** It shipped
 * hardcoded `disabled readOnly`, with a note saying it was not editable "from this screen" — but
 * no other screen offers it, so the flag was unreachable and Stripe could never be turned on at
 * all. The API had accepted it the whole time.
 *
 * Two things gate Stripe and they are set in different places: the keys are server configuration,
 * and this is a database row an admin can flip without a deploy. `StripeFeatureGate` requires both,
 * so turning this on before the keys are deployed changes nothing except what the organization
 * screens say — which is why it is safe to offer here on its own.
 */
export const BillingSettingsSection = () => {
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [stripeEnabled, setStripeEnabled] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const stripeHelpId = useId();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);

    getBillingSettings(controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setSettings(loaded);
        setStripeEnabled(loaded.stripeEnabled);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(describe(err, 'Could not load the billing settings.'));
        setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!settings || saving) return;

      setSaveError(null);
      setSaved(false);

      setSaving(true);
      try {
        const next = await saveBillingSettings({
          /*
            Carried back unchanged. This screen no longer edits the reminder schedule - every
            organization is billed through Stripe, which does its own chasing - but the endpoint is
            a PUT and omitting them would overwrite the stored values with defaults.
          */
          remindersEnabled: settings.remindersEnabled,
          daysBeforeDue: settings.daysBeforeDue,
          overdueEveryDays: settings.overdueEveryDays,
          stripeEnabled,
        });
        setSettings(next);
        setStripeEnabled(next.stripeEnabled);
        setSaved(true);
      } catch (err: unknown) {
        setSaveError(describe(err, 'Could not save the billing settings.'));
      } finally {
        setSaving(false);
      }
    },
    [stripeEnabled, saving, settings]
  );

  return (
    <AdminPage labelledBy="billing-settings-title">
      <AdminPageHeader
        title="Billing settings"
        titleId="billing-settings-title"
        subtitle="Whether this system talks to Stripe at all."
        back={
          <Link className="billing__settings-link" to="/admin/billing">
            Back to billing
          </Link>
        }
      />

      {loadError && (
        <div className="admin-error" role="alert">
          <span>{loadError}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setReloadToken((token) => token + 1)}
            data-testid="billing-settings-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {loading ? (
        <p className="admin-state">Loading the settings...</p>
      ) : settings ? (
        <AdminPanel>
          <form className="billing-form" onSubmit={handleSubmit} data-testid="billing-settings-form">
            {/*
              Editable, which it was not. It shipped hardcoded `disabled readOnly` with a note saying
              it is "not editable from this screen" — and there is no other screen. The API has always
              accepted it (SaveBillingSettingsRequest.StripeEnabled, written by SaveSettingsAsync), so
              the flag was simply unreachable and Stripe could never be switched on at all.

              Safe to expose on its own: the keys are configuration and this is a database flag, and
              StripeFeatureGate requires BOTH before anything talks to Stripe. Turning this on with no
              keys deployed changes nothing except what the organization panel says.
            */}
            <Checkbox
              label="Stripe payments"
              checked={stripeEnabled}
              onChange={(e) => setStripeEnabled(e.target.checked)}
              disabled={saving}
              aria-describedby={stripeHelpId}
              data-testid="billing-settings-stripe"
            />
            <p className="billing-panel__note" id={stripeHelpId}>
              Lets this system talk to Stripe: linking a customer, importing invoices, and recording
              what Stripe collects. It also needs the Stripe keys set on the server, so if the
              organization screens still say Stripe is off after saving, the keys are what is missing.
            </p>

            {saveError && (
              <p className="billing-form__error" role="alert" data-testid="billing-settings-error">
                {saveError}
              </p>
            )}

            {/* Always mounted, so the confirmation is announced when it appears. */}
            <p role="status" className="billing-panel__note" data-testid="billing-settings-status">
              {saved ? 'Settings saved.' : ''}
            </p>

            <div className="billing-panel__actions">
              <Button type="submit" loading={saving} data-testid="billing-settings-save">
                {saving ? 'Saving...' : 'Save settings'}
              </Button>
            </div>
          </form>
        </AdminPanel>
      ) : null}
    </AdminPage>
  );
};
